import { computed, inject, Injectable, signal } from '@angular/core';
import { deleteCookie, getCookie, setCookie } from '../../utils/cookie.util';
import { clearCustomerDataCache } from '../../utils/customer-cache.util';
import { SessionTimingService } from '../auth/session-timing.service';
import { FilingFormPersistenceService } from '../mbrs/filing-form-persistence.service';

/**
 * Cookie name housing the raw dev JWT. The value is the token string
 * as-is — the expiry is recovered client-side from the JWT's own `exp`
 * claim, so nothing else has to be stored.
 */
const DEV_SESSION_COOKIE = 'yyc_dev_session';

/**
 * Holds the dev-area JWT session state.
 *
 * Storage: a JS-readable cookie (NOT HttpOnly — impossible from the
 * browser). The cookie persists the JWT across page reloads, new tabs,
 * and browser restarts until the user explicitly signs out, the token
 * expires, or the server rejects it.
 *
 * Cookie layout: the value is the JWT as-is. Expiry is decoded from
 * the JWT's `exp` claim (Unix seconds, per RFC 7519) the moment the
 * token is stored or rehydrated — we never need to persist it
 * separately. The cookie's own `Max-Age` attribute is set from the
 * same `exp` so the browser auto-evicts past expiry.
 *
 * Why cookie over localStorage:
 *   - The cookie's own `Max-Age` serves as a cheap backup so a forgotten
 *     in-memory clearSession call can't leave the token resident forever.
 *   - Aligns with the rest of the app's auth model (cookie-based),
 *     even though this cookie is consumed by JS rather than being sent
 *     automatically to the server.
 *
 * Why not HttpOnly:
 *   - Impossible: the JWT is returned in the dev-login response body,
 *     so the frontend has to touch it to attach `Authorization: Bearer`.
 *   - Matches the reality that we already store the token in-process
 *     inside `devJwt` signal consumers; cookie storage doesn't
 *     meaningfully change the XSS surface.
 *
 * Signals stay the source of truth for consumers (guard, interceptor,
 * layout modal). The cookie is just the hydration backing store.
 */
@Injectable({ providedIn: 'root' })
export class DevSessionService {
  private readonly _devJwt = signal<string | null>(null);
  private readonly _expiresAtUtc = signal<string | null>(null);
  private readonly _sessionExpired = signal<boolean>(false);
  private clearing = false;

  /** TEMPORARY diagnostic: records how long the dev session lasted (non-prod). */
  private readonly sessionTiming = inject(SessionTimingService);
  private readonly filingPersistence = inject(FilingFormPersistenceService);

  readonly devJwt = this._devJwt.asReadonly();
  readonly expiresAtUtc = this._expiresAtUtc.asReadonly();
  readonly sessionExpired = this._sessionExpired.asReadonly();

  /** True when a token is stored and its `exp` is still in the future. */
  readonly isJwtValid = computed(() => {
    const token = this._devJwt();
    const exp = this._expiresAtUtc();
    if (!token || !exp) {
      return false;
    }
    const expMs = Date.parse(exp);
    if (Number.isNaN(expMs)) {
      return false;
    }
    return expMs > Date.now();
  });

  constructor() {
    this.rehydrateFromCookie();
  }

  setSession(token: string, expiresAtUtc: string): void {
    this._devJwt.set(token);
    this._expiresAtUtc.set(expiresAtUtc);
    this._sessionExpired.set(false);
    this.writeCookie(token, expiresAtUtc);
    // A developer session is taking over this tab — wipe any
    // customer-owned local/session storage so prior filing state
    // and pending-user-update lists can't leak across the boundary.
    clearCustomerDataCache();
    // Wipe filing-form IndexedDB state on teardown (clearCustomerDataCache only
    // covers localStorage/sessionStorage). Fire-and-forget — the dev takeover
    // must not block on storage. Uses the persistence service's already-open IDB
    // connection (clearAll on the store), never deleteDB.
    void this.filingPersistence.clearAll();
  }

  /**
   * Explicit user-initiated clear (sign-out, cookie-logout cascade).
   *
   * Customer-storage cleanup is intentionally NOT done here — it lives
   * in `AuthService.clearAuthState()` so every logout path (customer
   * voluntary, customer forced, dev sign-out, dev expiry) runs the
   * same wipe ceremony regardless of which service kicked it off.
   */
  clearSession(): void {
    if (this.clearing) {
      return;
    }
    this.clearing = true;
    try {
      this._devJwt.set(null);
      this._expiresAtUtc.set(null);
      deleteCookie(DEV_SESSION_COOKIE);
    } finally {
      this.clearing = false;
    }
  }

  /**
   * Called by the `devJwtInterceptor` when a dev endpoint responds 401
   * (the Bearer auth layer has explicitly rejected the token). Flips
   * `sessionExpired` so the layout can show the modal, then clears
   * the token. Idempotent.
   */
  markExpired(): void {
    if (this.clearing) {
      return;
    }
    // TEMPORARY diagnostic: silent dev-token expiry is the one dev session-end
    // path that does NOT funnel through AuthService (the devJwtInterceptor calls
    // this directly on a dev-endpoint 401), so the timing record is closed here.
    // markEnd is idempotent, so a 401 burst still records exactly one end. No-op
    // in production. See SessionTimingService.
    this.sessionTiming.markEnd('session-expired');
    this.clearSession();
    this._sessionExpired.set(true);
  }

  /** Called by the layout after the user dismisses the expiry modal. */
  acknowledgeExpiry(): void {
    this._sessionExpired.set(false);
  }

  private writeCookie(token: string, expiresAtUtc: string): void {
    const expMs = Date.parse(expiresAtUtc);
    const maxAgeSeconds = Number.isNaN(expMs)
      ? undefined
      : Math.max(0, Math.floor((expMs - Date.now()) / 1000));
    setCookie(DEV_SESSION_COOKIE, token, {
      maxAgeSeconds,
      sameSite: 'Strict',
    });
  }

  private rehydrateFromCookie(): void {
    const token = getCookie(DEV_SESSION_COOKIE);
    if (!token) {
      return;
    }
    const expSeconds = this.readJwtExp(token);
    if (expSeconds === null || expSeconds * 1000 <= Date.now()) {
      // Either the cookie is a JWT with an unreadable `exp`, or the
      // token is already past its expiry (the browser's Max-Age is a
      // backstop; explicit client-side check is the primary gate).
      deleteCookie(DEV_SESSION_COOKIE);
      return;
    }
    this._devJwt.set(token);
    this._expiresAtUtc.set(new Date(expSeconds * 1000).toISOString());
  }

  /**
   * Extracts the `exp` claim (Unix seconds, RFC 7519) from a JWT
   * without verifying its signature — the signature check is the
   * backend's job. Returns `null` for anything that doesn't look like
   * a well-formed JWT with a numeric `exp`.
   */
  private readJwtExp(jwt: string): number | null {
    const parts = jwt.split('.');
    if (parts.length < 2) {
      return null;
    }
    try {
      // base64url → base64 (swap URL-safe chars, restore padding).
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '==='.slice(0, (4 - (b64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded)) as { exp?: unknown };
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }
}
